import React, { useMemo } from 'react';
import { sampleRoaches } from '../../data/sampleRoachData';

const PulseStreakCalendar = ({ pulseContext = "Matrix" }) => {
  const history = sampleRoaches;

  const { calendarData, currentStreak } = useMemo(() => {
    if (!history || history.length === 0) {
      return { calendarData: [], currentStreak: 0 };
    }

    const grouped = {};

    history.forEach(item => {
      if (!item?.roachDate || !item?.pulseContext) return;
      const dateKey = String(item.roachDate).split('T')[0].split(' ')[0].trim();
      if (!grouped[dateKey]) grouped[dateKey] = new Set();
      grouped[dateKey].add(String(item.pulseContext).trim());
    });

    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    // Streak calculation
    const activeDates = sortedDates.filter(date =>
      pulseContext === "Matrix"
        ? grouped[date].size > 0
        : grouped[date]?.has(pulseContext) === true
    );

    let streak = activeDates.length > 0 ? 1 : 0;

    for (let i = 0; i < activeDates.length - 1; i++) {
      const curr = new Date(activeDates[i]);
      const next = new Date(activeDates[i + 1]);
      const dayDiff = Math.round((curr - next) / (1000 * 3600 * 24));
      if (dayDiff === 1) {
        streak++;
      } else {
        break;
      }
    }

    // Build calendar data
    const calendarData = sortedDates.map((date, index) => ({
      sn: index + 1,
      date: new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }),
      rawDate: date,
      contexts: pulseContext === "Matrix"
        ? ["Overall", "Home", "Work", "Campus", "Learning", "Social", "Environment"].map(ctx => ({
            name: ctx,
            present: grouped[date]?.has(ctx) || false
          }))
        : [{
            name: pulseContext,
            present: grouped[date]?.has(pulseContext) || false
          }]
    }));

    return { calendarData, currentStreak: streak };
  }, [history, pulseContext]);

  const isMatrix = pulseContext === "Matrix";

  return (
    <div className="space-y-4">
      {/* Dynamic Streak Counter */}
      <div className="bg-[#0f172a] p-6 rounded-3xl border border-gray-800 text-center">
        <div className="text-xl lg:text-2xl font-black text-yellow-400 tabular-nums">
          🔥 {currentStreak}
        </div>
        <div className="text-sm text-yellow-200 mt-1">
          Consecutive {isMatrix ? "Matrix" : pulseContext} Days
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-[#111827] border border-gray-700 rounded-xl overflow-hidden">
        <div className="p-3 lg:p-5 border-b border-gray-700">
          <h3 className="text-cyan-200 font-bold text-sm lg:text-lg flex items-center gap-3">
            {isMatrix ? "🔥 Full Pulse Matrix" : `🔥 ${pulseContext} Streak`}
            <span className="text-xs lg:text-sm font-normal text-gray-400">
              ({history.length} total pulses)
            </span>
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0f1c] sticky top-0 z-10">
              <tr className="border-b border-gray-700">
                <th className="text-xs lg:text-sm px-3 py-3 lg:px-5 lg:py-4 text-left w-12 text-gray-200">SN</th>
                <th className="text-xs lg:text-sm px-3 py-3 lg:px-5 lg:py-4 text-left text-gray-200">Date</th>

                {isMatrix ? (
                  ["Overall", "Home", "Work", "Campus", "Learning", "Social", "Environment"].map(ctx => (
                    <th key={ctx} className="text-[11px] lg:text-xs px-2 py-3 lg:px-4 lg:py-4 text-center text-gray-400 whitespace-nowrap">
                      {ctx.slice(0, 4)}
                    </th>
                  ))
                ) : (
                  <th className="text-xs lg:text-sm px-4 py-4 text-center text-cyan-300 font-medium">
                    {pulseContext}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {calendarData.map(row => (
                <tr key={row.rawDate} className="hover:bg-gray-900/50">
                  <td className="text-xs lg:text-sm px-3 py-3 lg:px-5 lg:py-4 text-gray-200 font-mono">{row.sn}</td>
                  <td className="text-xs lg:text-sm px-3 py-3 lg:px-5 lg:py-4 font-medium text-gray-100">{row.date}</td>

                  {row.contexts.map((ctx, i) => (
                    <td key={i} className="text-base lg:text-2xl px-3 py-3 lg:px-5 lg:py-4 text-center">
                      {ctx.present ? "✔️" : "–"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PulseStreakCalendar;